// A searchable select on Popover + Command, with Mantine Select's rules: the list shows the options whose label
// contains the trimmed search (any case), and picking the selected option again clears it (`allowDeselect`, on by
// default; `allowDeselect={false}` hands the same value back instead, so `onChange` never gets a null from a pick).
//
//   <Combobox
//     options={importirs}                  // { value, label }[]
//     value={npwp}                         // string | null
//     onChange={setNpwp}                   // null when cleared or deselected
//     placeholder={t('…pickImportir')}     // trigger text while nothing is selected; also the search placeholder
//     emptyText={t('…noImportir')}         // shown when the search matches nothing (none by default)
//     clearable clearLabel={t('…clear')}   // the × button (`field` only: a type error with `variant="button"`)
//   />
//
// Two looks, one per page it came from (revamp cleanup PR). Each renders that page's markup exactly:
// - `field` (default): looks like an Input. A ChevronDown, an optional × button, the check mark at the end of the
//   selected item, an "empty" line, and `aria-controls` on the trigger. From master/customer-email-forwarding.
// - `button`: the outline Button of shadcn's combobox. ChevronsUpDown, the check mark before every item (shown on
//   the selected one), and cmdk's own filtering (an empty search result hides the group). From
//   customer-approval/email-groups.
// The trigger takes `id`, `disabled`, `required` (aria-required), `invalid` (aria-invalid), `aria-label`,
// `aria-describedby`, `onFocus` and `onBlur` (the trigger's own: it also blurs when the list opens, as `ui/combobox`'s
// did); `className` goes on the outer element (the field's wrapper, the button itself).
import { Check, ChevronDown, ChevronsUpDown, X } from 'lucide-react';
import { type FocusEventHandler, type ReactNode, useId, useState } from 'react';

import { Button } from '@/components/lumen/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/lumen/command';
import { controlClasses } from '@/components/lumen/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/lumen/popover';
import { cn } from '@/lib/utils';
import { type ComboboxOption, cmdkFilter, filterOptions, pickValue } from './combobox-logic';

export type { ComboboxOption } from './combobox-logic';

/** `clearable` shows the × button while a value is selected; `clearLabel` is its name. */
type ClearProps = { clearable?: false; clearLabel?: never } | { clearable: true; clearLabel: string };

/**
 * `field` (default) looks like an Input; `button` like an outline Button. See the top of the file. Only `field` has
 * the × button, so `clearable` and `clearLabel` exist only there.
 */
type VariantProps = ({ variant?: 'field' } & ClearProps) | { variant: 'button'; clearable?: never; clearLabel?: never };

export type ComboboxProps = VariantProps & {
  options: ComboboxOption[];
  value: string | null;
  /** The new value: `null` when the × button clears it or the selected option is picked again (`allowDeselect`). */
  onChange: (value: string | null) => void;
  /** Picking the selected option again clears it (default `true`); `false` calls `onChange` with the same value. */
  allowDeselect?: boolean;
  /** The trigger's text while nothing is selected. */
  placeholder: string;
  /** The search box's placeholder. Default: `placeholder`. */
  searchPlaceholder?: string;
  /** Shown when the search matches nothing. Without it the list is just empty. */
  emptyText?: ReactNode;
  id?: string;
  disabled?: boolean;
  required?: boolean;
  invalid?: boolean;
  'aria-label'?: string;
  'aria-describedby'?: string;
  className?: string;
  /** On the trigger button. */
  onFocus?: FocusEventHandler<HTMLButtonElement>;
  /** On the trigger button. */
  onBlur?: FocusEventHandler<HTMLButtonElement>;
};

export function Combobox({
  options,
  value,
  onChange,
  allowDeselect = true,
  placeholder,
  searchPlaceholder = placeholder,
  emptyText,
  clearable,
  clearLabel,
  variant = 'field',
  id,
  disabled,
  required,
  invalid,
  'aria-label': ariaLabel,
  'aria-describedby': describedBy,
  className,
  onFocus,
  onBlur,
}: ComboboxProps) {
  const listId = `${useId()}-list`;
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const selected = options.find((option) => option.value === value);
  const isField = variant === 'field';
  const showClear = isField && clearable && value !== null && !disabled;

  const pick = (option: ComboboxOption) => {
    onChange(pickValue(value, option.value, allowDeselect));
    setOpen(false);
    setSearch('');
  };

  const trigger = isField ? (
    <div className={cn('relative w-full', className)}>
      <PopoverTrigger asChild>
        <button
          id={id}
          type="button"
          role="combobox"
          aria-expanded={open}
          aria-controls={listId}
          aria-label={ariaLabel}
          aria-required={required || undefined}
          aria-invalid={invalid || undefined}
          aria-describedby={describedBy}
          disabled={disabled}
          onFocus={onFocus}
          onBlur={onBlur}
          className={cn(
            controlClasses,
            'flex h-9 items-center gap-2 px-3 text-left text-sm',
            showClear ? 'pr-15' : 'pr-9',
            !selected && 'text-fg-muted'
          )}
        >
          <span className="min-w-0 flex-1 truncate">{selected?.label ?? placeholder}</span>
        </button>
      </PopoverTrigger>
      {showClear && (
        <Button
          type="button"
          variant="ghost"
          size="icon-xs"
          className="absolute top-1/2 right-7 -translate-y-1/2"
          aria-label={clearLabel}
          onClick={() => onChange(null)}
        >
          <X aria-hidden />
        </Button>
      )}
      <ChevronDown
        aria-hidden
        className="pointer-events-none absolute top-1/2 right-3 size-4 -translate-y-1/2 text-fg-muted"
      />
    </div>
  ) : (
    <PopoverTrigger asChild>
      <Button
        id={id}
        type="button"
        variant="outline"
        role="combobox"
        aria-expanded={open}
        aria-label={ariaLabel}
        aria-required={required || undefined}
        aria-invalid={invalid || undefined}
        aria-describedby={describedBy}
        disabled={disabled}
        onFocus={onFocus}
        onBlur={onBlur}
        className={cn('w-full justify-between px-3 font-normal', className)}
      >
        <span className={cn('truncate', !selected && 'text-fg-muted')}>{selected?.label ?? placeholder}</span>
        <ChevronsUpDown aria-hidden className="text-fg-muted" />
      </Button>
    </PopoverTrigger>
  );

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) setSearch('');
      }}
    >
      {trigger}
      <PopoverContent
        // `field` names the list itself; `button` keeps Radix's own content id (an `id: undefined` would erase it).
        {...(isField ? { id: listId } : {})}
        align="start"
        className="w-(--radix-popover-trigger-width) min-w-72 p-0"
      >
        {isField ? (
          <Command shouldFilter={false}>
            <CommandInput value={search} onValueChange={setSearch} placeholder={searchPlaceholder} />
            <CommandList>
              {emptyText !== undefined && <CommandEmpty>{emptyText}</CommandEmpty>}
              <CommandGroup>
                {filterOptions(options, search).map((option) => (
                  <CommandItem key={option.value} value={option.value} onSelect={() => pick(option)}>
                    <span className="min-w-0 flex-1">{option.label}</span>
                    {option.value === value && <Check aria-hidden className="text-label" />}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        ) : (
          <Command filter={cmdkFilter}>
            <CommandInput placeholder={searchPlaceholder} />
            <CommandList>
              {emptyText !== undefined && <CommandEmpty>{emptyText}</CommandEmpty>}
              <CommandGroup>
                {options.map((option) => (
                  <CommandItem
                    key={option.value}
                    value={option.value}
                    keywords={[option.label]}
                    onSelect={() => pick(option)}
                  >
                    <Check aria-hidden className={cn(option.value === value ? 'opacity-100' : 'opacity-0')} />
                    {option.label}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        )}
      </PopoverContent>
    </Popover>
  );
}
